import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel, FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';

import {getBranches} from "../../../http/clinicAPI";
import {fetchSpecializations} from "../../../http/doctorAPI";
import {fetchSurveys} from "../../../http/surveyAPI";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {getImageUrl} from "../../../helpers/getImageUrl";
import {useTriggerTypes} from "../hooks/useTriggerTypes";
import ServiceSelector from "./ServiceSelector";
import Editor from "../../../components/Editor";
import {changeForDB, changeForEditor, getPlainTextLength} from "../../../helpers/editorFunc";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function TriggerForm({trigger, onSave, onCancel, clinic}) {
    const [title, setTitle] = useState('');
    const [templateText, setTemplateText] = useState('');

    const triggerTypes = useTriggerTypes();
    const [type, setType] = useState(Object.keys(triggerTypes)[0]);
    const [image, setImage] = useState(null);
    const [needRemoveImage, setNeedRemoveImage] = useState(null);
    const [pointsCount, setPointsCount] = useState(0);
    const [params, setParams] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const [branches, setBranches] = useState([]);
    const [sendToAllBranches, setSendToAllBranches] = useState(true);
    const [selectedBranchId, setSelectedBranchId] = useState('');
    const [isEditing, setIsEditing] = useState(!!trigger);
    const [errors, setErrors] = useState({});
    const [clinicOffset, setClinicOffset] = useState(null);
    const [isBonusEnabled, setIsBonusEnabled] = useState(pointsCount > 0);
    const [separateBonusBurningEnabled, setSeparateBonusBurningEnabled] = useState(trigger?.separateBonusBurningEnabled || false);
    const [daysBeforeBurning, setDaysBeforeBurning] = useState(trigger?.daysBeforeBurning || '');
    const [isBurnEnabled, setIsBurnEnabled] = useState(!!params.bonusBurnDays);
    const [specializations, setSpecializations] = useState([]);
    const [surveys, setSurveys] = useState([]);

    useEffect(() => {
        if (clinic?.timezoneOffset) {
            setClinicOffset(-parseInt(clinic.timezoneOffset));
        }
    }, [clinic]);

    useEffect(() => {
        fetchBranches();
        fetchSpecializationsData();
        fetchSurveysData();
    }, []);

    useEffect(() => {
        if (trigger) {
            // Заполняем форму
            setIsEditing(true);
            setTitle(trigger.title || '');
            setTemplateText(changeForEditor(trigger.templateText || ''));
            setType(trigger.type || Object.keys(triggerTypes)[0]);
            setPointsCount(trigger.pointsCount || 0);
            setParams(trigger.params || {});
            setImage(null);
            setSendToAllBranches(trigger.sendToAllBranches ?? true);
            setSelectedBranchId(trigger?.branchId ?? '');

            // Если есть имя изображения в trigger и нет нового загруженного файла — показываем превью из API
            if (trigger.imageName) {
                setImagePreview(getImageUrl(trigger.imageName));
            } else {
                setImagePreview(null);
            }

            setSeparateBonusBurningEnabled(!!trigger.separateBonusBurningEnabled);
            setDaysBeforeBurning(trigger.daysBeforeBurning ?? '');
        } else {
            // Очистка формы
            setIsEditing(false);

            setTitle('');
            setTemplateText('');
            setType(Object.keys(triggerTypes)[0]);
            setPointsCount(0);
            setParams({});
            setImage(null);
            setImagePreview(null);
            setSendToAllBranches(true);
            setSelectedBranchId('');
            setSeparateBonusBurningEnabled(false);
            setDaysBeforeBurning('');
        }
        setErrors({});
    }, [trigger]);

    useEffect(() => {
        setIsBonusEnabled(pointsCount > 0);
    }, [pointsCount]);

    useEffect(() => {
        setIsBurnEnabled(!!params.bonusBurnDays);
    }, [params.bonusBurnDays]);

    useEffect(() => {
        if (type === 'REMIND_BONUS_BURN') setSendToAllBranches(true);
    }, [type]);

    // Очистка URL.createObjectURL для предотвращения утечек памяти
    useEffect(() => {
        return () => {
            if (imagePreview && image instanceof File) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview, image]);

    const fetchBranches = async () => {
        const branches = await getBranches()
        setBranches(branches);
    };

    const fetchSpecializationsData = async () => {
        const specs = await fetchSpecializations();
        setSpecializations(specs);
    };

    const fetchSurveysData = async () => {
        const surveysData = await fetchSurveys();
        setSurveys(surveysData);
    };

    // Проверка валидности формы
    function validate() {
        const newErrors = {};

        if (!title.trim()) {
            newErrors.title = 'Заголовок обязателен';
        }
        if (!templateText.trim()) {
            newErrors.templateText = 'Текст сообщения обязателен';
        }
        if (!type) {
            newErrors.type = 'Тип триггера обязателен';
        }
        if (!sendToAllBranches && !selectedBranchId) {
            newErrors.branchId = 'Выберите филиал или включите рассылку по всем филиалам';
        }

        // Валидация времени для определённых триггеров
        const triggersWithTime = [
            'BIRTHDAY',
            'PETS_BIRTHDAY',
            'FIRST_APPOINTMENT',
            'INACTIVE_PATIENT',
            'REGULAR_PATIENT',
            'REMIND_APPOINTMENT',
            'PROMO_END',
            'REMIND_BONUS_BURN',
            'REMIND_BONUS_COUNT'
        ];
        if (triggersWithTime.includes(type)) {
            if (!params.time) {
                params.time = {hour: 9, minute: 0};
            }
            if (!params.time || params.time.hour === undefined || params.time.hour < 8 || params.time.hour > 22) {
                newErrors.time = 'Укажите корректный час (8-22)';
            } else if (
                params.time.minute === undefined ||
                params.time.minute < 0 ||
                params.time.minute > 59
            ) {
                newErrors.time = 'Укажите корректные минуты (0-59)';
            }
        }

        // Остальная валидация параметров (без изменений)
        switch (type) {
            case 'REGULAR_PATIENT':
                if (
                    params.appointmentsForPeriod === undefined ||
                    params.appointmentsForPeriod === '' ||
                    isNaN(Number(params.appointmentsForPeriod)) ||
                    params.appointmentsForPeriod < 0
                ) {
                    newErrors.appointmentsForPeriod = 'Введите корректное количество приёмов';
                }
                if (!params.periodType) {
                    newErrors.periodType = 'Выберите период';
                }
                break;
            case 'FIRST_APPOINTMENT':
                if (
                    params.daysAfterFirstAppointment === undefined ||
                    params.daysAfterFirstAppointment === '' ||
                    isNaN(Number(params.daysAfterFirstAppointment)) ||
                    params.daysAfterFirstAppointment < 0
                ) {
                    newErrors.daysAfterFirstAppointment = 'Введите корректное количество дней';
                }
                break;
            case 'DATE':
                if (!params.date) {
                    newErrors.date = 'Дата обязательна';
                }
                if (params.isRepeatable) {
                    if (
                        params.intervalValue === undefined ||
                        params.intervalValue === '' ||
                        isNaN(Number(params.intervalValue)) ||
                        params.intervalValue < 1
                    ) {
                        newErrors.intervalValue = 'Введите корректный интервал';
                    }
                    if (!params.intervalUnit) {
                        newErrors.intervalUnit = 'Выберите единицу интервала';
                    }
                }
                if (params.minAge !== undefined && params.minAge !== '' && (isNaN(Number(params.minAge)) || Number(params.minAge) < 0)) {
                    newErrors.minAge = 'Введите корректный минимальный возраст (0 или больше)';
                }
                if (params.maxAge !== undefined && params.maxAge !== '' && (isNaN(Number(params.maxAge)) || Number(params.maxAge) < 0)) {
                    newErrors.maxAge = 'Введите корректный максимальный возраст (0 или больше)';
                }
                if (
                    params.minAge !== undefined && params.minAge !== '' &&
                    params.maxAge !== undefined && params.maxAge !== '' &&
                    Number(params.minAge) > Number(params.maxAge)
                ) {
                    newErrors.maxAge = 'Максимальный возраст должен быть больше или равен минимальному';
                }
                break;
            case 'INACTIVE_PATIENT':
                if (
                    params.daysAfterLastAppointment === undefined ||
                    params.daysAfterLastAppointment === '' ||
                    isNaN(Number(params.daysAfterLastAppointment)) ||
                    params.daysAfterLastAppointment < 0
                ) {
                    newErrors.daysAfterLastAppointment = 'Введите корректное количество дней';
                }
                break;
            case 'BIRTHDAY':
            case 'PETS_BIRTHDAY':
                if (
                    params.daysBefore === undefined ||
                    params.daysBefore === '' ||
                    isNaN(Number(params.daysBefore)) ||
                    params.daysBefore < 0
                ) {
                    newErrors.daysBefore = 'Введите корректное количество дней';
                }
                break;
            case 'REMIND_APPOINTMENT':
                if (
                    params.daysBefore === undefined ||
                    params.daysBefore === '' ||
                    isNaN(Number(params.daysBefore)) ||
                    params.daysBefore < 1
                ) {
                    newErrors.daysBefore = 'Введите корректное количество дней (минимум 1)';
                }
                break;
            case 'REPEAT_APPOINTMENT':
                if (
                    params.daysAfterAppointment === undefined ||
                    params.daysAfterAppointment === '' ||
                    isNaN(Number(params.daysAfterAppointment)) ||
                    params.daysAfterAppointment < 1
                ) {
                    newErrors.daysAfterAppointment = 'Введите корректное количество дней (минимум 1)';
                }
                if (!params.serviceIds || params.serviceIds.length === 0) {
                    newErrors.serviceIds = 'Выберите хотя бы одну услугу';
                }
                break;
            case 'SURVEY_AFTER_APPOINTMENT':
                if (!params.surveyId) {
                    newErrors.surveyId = 'Выберите опрос';
                }
                if (!params.serviceIds || params.serviceIds.length === 0) {
                    newErrors.serviceIds = 'Выберите хотя бы одну услугу';
                }
                break;
            case 'DRINKS_BEFORE_APPOINTMENT':
                if (
                    params.minutesBeforeAppointment === undefined ||
                    params.minutesBeforeAppointment === '' ||
                    isNaN(Number(params.minutesBeforeAppointment)) ||
                    params.minutesBeforeAppointment < 5
                ) {
                    newErrors.minutesBeforeAppointment = 'Введите корректное количество минут (минимум 5)';
                }
                break;
            case 'REMIND_APPOINTMENT_SAME_DAY':
                if (
                    params.minutesBeforeAppointment === undefined ||
                    params.minutesBeforeAppointment === '' ||
                    isNaN(Number(params.minutesBeforeAppointment)) ||
                    params.minutesBeforeAppointment < 5
                ) {
                    newErrors.minutesBeforeAppointment = 'Введите корректное количество минут (минимум 5)';
                }
                break;
            case 'PROMO_END':
                if (
                    params.notifyBeforeDays === undefined ||
                    params.notifyBeforeDays === '' ||
                    isNaN(Number(params.notifyBeforeDays)) ||
                    params.notifyBeforeDays < 0
                ) {
                    newErrors.notifyBeforeDays = 'Введите корректное количество дней';
                }
                break;
            case 'REMIND_BONUS_BURN':
                if (
                    params.notifyBurnBonusBeforeDays === undefined ||
                    params.notifyBurnBonusBeforeDays === '' ||
                    isNaN(Number(params.notifyBurnBonusBeforeDays)) ||
                    Number(params.notifyBurnBonusBeforeDays) < 0
                ) {
                    newErrors.notifyBurnBonusBeforeDays = 'Введите корректное количество дней (0 или больше)';
                }
                if (!params.time || params.time.hour === undefined || params.time.minute === undefined) {
                    newErrors.time = 'Укажите время отправки';
                } else if (
                    params.time.hour < 0 || params.time.hour > 23 ||
                    params.time.minute < 0 || params.time.minute > 59
                ) {
                    newErrors.time = 'Укажите корректное время';
                }
                break;

            case 'REMIND_BONUS_COUNT':
                if (
                    params.bonusCount === undefined ||
                    params.bonusCount === '' ||
                    params.bonusCount < 100 ||
                    isNaN(Number(params.bonusCount))
                ) {
                    newErrors.bonusCount = 'Минимальное количество бонусов — 100';
                }
                if (
                    params.daysAfterLastBonus === undefined ||
                    params.daysAfterLastBonus === '' ||
                    params.daysAfterLastBonus < 0 ||
                    isNaN(Number(params.daysAfterLastBonus))
                ) {
                    newErrors.daysAfterLastBonus = 'Введите корректное количество дней (0 или больше)';
                }
                if (
                    params.repeatEveryDays === undefined ||
                    params.repeatEveryDays === '' ||
                    params.repeatEveryDays < 1 ||
                    isNaN(Number(params.repeatEveryDays))
                ) {
                    newErrors.repeatEveryDays = 'Минимум через 1 день';
                }
                if (!params.time || params.time.hour === undefined || params.time.minute === undefined) {
                    newErrors.time = 'Укажите время отправки';
                } else if (
                    params.time.hour < 0 || params.time.hour > 23 ||
                    params.time.minute < 0 || params.time.minute > 59
                ) {
                    newErrors.time = 'Укажите корректное время';
                }
                break;
        }

        if (isBonusEnabled && separateBonusBurningEnabled) {
            if (!daysBeforeBurning || Number(daysBeforeBurning) < 1) {
                newErrors.daysBeforeBurning = 'Введите корректное количество дней (> 0)';
            }
        }
        console.log(newErrors);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleSubmit = e => {
        e.preventDefault();

        if (!validate()) {
            return; // есть ошибки — не отправляем
        }

// Преобразование даты для типа DATE
        let updatedParams = {...params};

        if (type === 'DATE' && params.date) {
            // Просто сохраняем как есть, без UTC
            updatedParams.date = dayjs(params.date).format('YYYY-MM-DD HH:mm:ss');
        }

        if (!isBonusEnabled) {
            updatedParams.bonusBurnDays = undefined;
        }
        if (!isBurnEnabled) {
            updatedParams.bonusBurnDays = undefined;
        }

        if (type === 'DATE' && !updatedParams.gender) {
            delete updatedParams.gender;
        }
        if (type === 'DATE' && (updatedParams.minAge === '' || updatedParams.minAge === undefined || updatedParams.minAge === null)) {
            delete updatedParams.minAge;
        }
        if (type === 'DATE' && (updatedParams.maxAge === '' || updatedParams.maxAge === undefined || updatedParams.maxAge === null)) {
            delete updatedParams.maxAge;
        }

        const templateTextForSave = changeForDB(templateText || '');

        onSave(
            {
                title,
                templateText: templateTextForSave,
                type,
                pointsCount: type === 'SURVEY_AFTER_APPOINTMENT' ? 0 : pointsCount,
                params: updatedParams,
                needRemoveImage: type === 'SURVEY_AFTER_APPOINTMENT' ? true : (!imagePreview && !!trigger?.imageName),
                branchId: sendToAllBranches ? null : selectedBranchId || null,
                sendToAllBranches,
                separateBonusBurningEnabled: !!(isBonusEnabled && separateBonusBurningEnabled),
                daysBeforeBurning: isBonusEnabled && separateBonusBurningEnabled ? Number(daysBeforeBurning) : undefined,
            },
            type === 'SURVEY_AFTER_APPOINTMENT' ? null : image
        );
    };

    // Вспомогательные функции для обновления параметров
    const updateParam = (key, value) => {
        console.log(key, value);
        setParams(prev => ({...prev, [key]: value}));
        // при изменении убираем ошибку для этого поля
        setErrors(prev => {
            const newErrors = {...prev};
            delete newErrors[key];
            return newErrors;
        });
    };

    // Аналогично для основных полей:
    const handleTitleChange = e => {
        setTitle(e.target.value);
        setErrors(prev => {
            const newErrors = {...prev};
            delete newErrors.title;
            return newErrors;
        });
    };

    const handleTemplateTextEditorChange = (value) => {
        setTemplateText(value);
        setErrors(prev => {
            const newErrors = {...prev};
            delete newErrors.templateText;
            return newErrors;
        });
    };

    const handleTypeChange = e => {
        const newType = e.target.value;
        setType(newType);
        setErrors(prev => {
            const newErrors = {...prev};
            delete newErrors.type;
            return newErrors;
        });
        setParams({}); // сброс параметров при смене типа

        // Для триггера опроса - сбрасываем бонусы и изображение
        if (newType === 'SURVEY_AFTER_APPOINTMENT') {
            setPointsCount(0);
            setIsBonusEnabled(false);
            setSeparateBonusBurningEnabled(false);
            setDaysBeforeBurning('');
            setImage(null);
            setImagePreview(null);
        }
    };

    const handlePointsCountChange = e => {
        setPointsCount(Number(e.target.value));
    };

    // Обновляем preview при выборе нового файла
    const handleImageChange = e => {
        const file = e.target.files[0] || null;
        
        // Проверка на размер файла (макс 3MB)
        if (file && file.size > 3 * 1024 * 1024) {
            setErrors(prev => ({
                ...prev,
                image: 'Размер изображения не должен превышать 3 МБ'
            }));
            e.target.value = ''; // Очищаем input
            return;
        }
        
        // Очищаем ошибку, если файл прошел проверку
        setErrors(prev => {
            const newErrors = {...prev};
            delete newErrors.image;
            return newErrors;
        });
        
        setImage(file);

        if (file) {
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        } else {
            // Если файл удалили, если есть триггер с imageName — показываем его
            if (trigger && trigger.imageName) {
                setImagePreview(getImageUrl(trigger.imageName));
            } else {
                setImagePreview(null);
            }
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
        // Очищаем ошибку при удалении изображения
        setErrors(prev => {
            const newErrors = {...prev};
            delete newErrors.image;
            return newErrors;
        });
    };

    // Вспомогательная функция для форматирования времени
    const formatTime = () => {
        if (!params.time) return '09:00';
        const hour = String(params.time.hour || 9).padStart(2, '0');
        const minute = String(params.time.minute || 0).padStart(2, '0');
        return `${hour}:${minute}`;
    };

    // Вспомогательная функция для обработки изменения времени
    const handleTimeChange = (e) => {
        const [hour, minute] = e.target.value.split(':').map(Number);
        updateParam('time', {hour, minute});
    };


    // Рендерим специфичные поля для каждого типа триггера с error и helperText
    function renderParamsFields() {
        switch (type) {
            case 'REMIND_BONUS_COUNT':
                return (
                    <>
                        <TextField
                            label="Минимальное количество бонусов (мин. 100)"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.bonusCount ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(100, Number(e.target.value));
                                updateParam('bonusCount', value);
                            }}
                            error={!!errors.bonusCount}
                            helperText={errors.bonusCount}
                            inputProps={{min: 100}}
                        />
                        <TextField
                            label="Дней после последнего начисления бонусов"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.daysAfterLastBonus ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                                updateParam('daysAfterLastBonus', value);
                            }}
                            error={!!errors.daysAfterLastBonus}
                            helperText={errors.daysAfterLastBonus}
                            inputProps={{min: 0}}
                        />
                        <TextField
                            label="Повторять спустя (дней) после последнего напоминания"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.repeatEveryDays ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(1, Number(e.target.value));
                                updateParam('repeatEveryDays', value);
                            }}
                            error={!!errors.repeatEveryDays}
                            helperText={errors.repeatEveryDays}
                            inputProps={{min: 1}}
                        />
                        <TextField
                            label="Время отправки (чч:мм)"
                            type="time"
                            fullWidth
                            required
                            margin="normal"
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time || 'Указывайте время в часовом поясе клиники'}
                            inputProps={{step: 60}}
                        />
                    </>
                );
            case 'REGULAR_PATIENT':
                return (
                    <>
                        <TextField
                            label="Количество приёмов за период"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.appointmentsForPeriod ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                                updateParam('appointmentsForPeriod', value);
                            }}
                            inputProps={{min: 0}}
                            error={!!errors.appointmentsForPeriod}
                            helperText={
                                errors.appointmentsForPeriod ||
                                'Триггер будет отправлен, если кол-во приемов у пользователя равно или больше указанной цифры'
                            }
                        />
                        <FormControl fullWidth margin="normal" required error={!!errors.periodType}>
                            <InputLabel id="period-type-label">Период</InputLabel>
                            <Select
                                labelId="period-type-label"
                                value={params.periodType || ''}
                                label="Период"
                                onChange={e => updateParam('periodType', e.target.value)}
                            >
                                <MenuItem value="HALF_YEAR">6 месяцев</MenuItem>
                                <MenuItem value="YEAR">1 год</MenuItem>
                            </Select>
                            {errors.periodType && (
                                <Box sx={{color: 'error.main', fontSize: '0.75rem', marginTop: '4px'}}>
                                    {errors.periodType}
                                </Box>
                            )}
                        </FormControl>
                        <TextField
                            label="Время отправки"
                            type="time"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{shrink: true}}
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time || 'Указывайте время в часовом поясе клиники'}
                        />
                    </>
                );
            case 'FIRST_APPOINTMENT':
                return (
                    <>
                        <TextField
                            label="Через сколько дней после первого приёма"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.daysAfterFirstAppointment ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                                updateParam('daysAfterFirstAppointment', value);
                            }}
                            inputProps={{min: 0}}
                            error={!!errors.daysAfterFirstAppointment}
                            helperText={errors.daysAfterFirstAppointment}
                        />
                        <TextField
                            label="Время отправки"
                            type="time"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{shrink: true}}
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time || 'Указывайте время в часовом поясе клиники'}
                        />
                    </>
                );
            case 'DATE':
                return (
                    <>
                        <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-start'}}>
                            <TextField
                                label="Дата"
                                type="datetime-local"
                                fullWidth
                                margin="normal"
                                InputLabelProps={{shrink: true}}
                                value={
                                    params.date
                                        ? dayjs(params.date, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DDTHH:mm')
                                        : ''
                                }
                                onChange={(e) => {
                                    const localDate = e.target.value;
                                    if (localDate) {
                                        const formatted = dayjs(localDate).format('YYYY-MM-DD HH:mm:ss');
                                        updateParam('date', formatted);
                                    }
                                }}
                                error={!!errors.date}
                                helperText={errors.date}
                            />
                            {params.date && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => updateParam('date', '')}
                                    sx={{mt: 2, minWidth: 'auto'}}
                                >
                                    Очистить
                                </Button>
                            )}
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!params.isRepeatable}
                                    onChange={e => updateParam('isRepeatable', e.target.checked)}
                                />
                            }
                            label="Повторять"
                        />
                        {params.isRepeatable && (
                            <>
                                <TextField
                                    label="Интервал повторения"
                                    type="number"
                                    fullWidth
                                    required={params.isRepeatable}
                                    margin="normal"
                                    value={params.intervalValue ?? ''}
                                    onChange={e => {
                                        const value = e.target.value === '' ? '' : Math.max(1, Number(e.target.value));
                                        updateParam('intervalValue', value);
                                    }}
                                    inputProps={{min: 1}}
                                    error={!!errors.intervalValue}
                                    helperText={errors.intervalValue}
                                />
                                <FormControl fullWidth margin="normal" required={params.isRepeatable}
                                             error={!!errors.intervalUnit}>
                                    <InputLabel id="interval-unit-label">Единица интервала</InputLabel>
                                    <Select
                                        labelId="interval-unit-label"
                                        value={params.intervalUnit || ''}
                                        label="Единица интервала"
                                        onChange={e => updateParam('intervalUnit', e.target.value)}
                                    >
                                        <MenuItem value="days">Дни</MenuItem>
                                        <MenuItem value="weeks">Недели</MenuItem>
                                        <MenuItem value="months">Месяцы</MenuItem>
                                    </Select>
                                    {errors.intervalUnit && (
                                        <Box sx={{color: 'error.main', fontSize: '0.75rem', marginTop: '4px'}}>
                                            {errors.intervalUnit}
                                        </Box>
                                    )}
                                </FormControl>
                            </>
                        )}
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="gender-label">Пол пользователя</InputLabel>
                            <Select
                                labelId="gender-label"
                                value={params.gender || ''}
                                label="Пол пользователя"
                                onChange={e => updateParam('gender', e.target.value)}
                            >
                                <MenuItem value="">Все</MenuItem>
                                <MenuItem value="male">Мужской</MenuItem>
                                <MenuItem value="female">Женский</MenuItem>
                            </Select>
                            <FormHelperText>
                              Если у пользователя не указан пол, то он все равно получит рассылку
                            </FormHelperText>
                        </FormControl>
                        <Box sx={{display: 'flex', gap: 2}}>
                            <TextField
                                label="Минимальный возраст"
                                type="number"
                                fullWidth
                                margin="normal"
                                value={params.minAge ?? ''}
                                onChange={e => updateParam('minAge', e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                                inputProps={{min: 0}}
                                error={!!errors.minAge}
                                helperText={errors.minAge || 'Граница включительная; без даты рождения тоже получат'}
                            />
                            <TextField
                                label="Максимальный возраст"
                                type="number"
                                fullWidth
                                margin="normal"
                                value={params.maxAge ?? ''}
                                onChange={e => updateParam('maxAge', e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                                inputProps={{min: 0}}
                                error={!!errors.maxAge}
                                helperText={errors.maxAge || 'Граница включительная; без даты рождения тоже получат'}
                            />
                        </Box>
                    </>
                );
            case 'INACTIVE_PATIENT':
                return (
                    <>
                        <TextField
                            label="Дней после последнего приёма"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.daysAfterLastAppointment ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                                updateParam('daysAfterLastAppointment', value);
                            }}
                            inputProps={{min: 0}}
                            error={!!errors.daysAfterLastAppointment}
                            helperText={errors.daysAfterLastAppointment}
                        />
                        <TextField
                            label="Время отправки"
                            type="time"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{shrink: true}}
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time || 'Указывайте время в часовом поясе клиники'}
                        />
                    </>
                );
            case 'BIRTHDAY':
            case 'PETS_BIRTHDAY':
                return (
                    <>
                        <TextField
                            label="За сколько дней до дня рождения"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.daysBefore ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                                updateParam('daysBefore', value);
                            }}
                            inputProps={{min: 0}}
                            error={!!errors.daysBefore}
                            helperText={errors.daysBefore}
                        />
                        <TextField
                            label={"Время отправки"}
                            type="time"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{shrink: true}}
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time || 'Указывайте время в часовом поясе клиники'}
                        />
                    </>
                );

            case 'REMIND_APPOINTMENT':
                return (
                    <>
                        <TextField
                            label="За сколько дней напомнить"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.daysBefore ?? 1}
                            onChange={e => {
                                const value = e.target.value === '' ? 1 : Math.max(1, Number(e.target.value));
                                updateParam('daysBefore', value);
                            }}
                            inputProps={{min: 1}}
                            error={!!errors.daysBefore}
                            helperText={errors.daysBefore || 'По умолчанию: 1 день (накануне)'}
                        />
                        <TextField
                            label="Время отправки"
                            type="time"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{shrink: true}}
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time || 'Указывайте время в часовом поясе клиники'}
                        />
                    </>
                );
            case 'REMIND_BONUS_BURN':
                return (
                    <>
                        <TextField
                            label="За сколько дней напомнить о сгорании бонусов"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.notifyBurnBonusBeforeDays ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                                updateParam('notifyBurnBonusBeforeDays', value);
                            }}
                            error={!!errors.notifyBurnBonusBeforeDays}
                            helperText={errors.notifyBurnBonusBeforeDays}
                            inputProps={{min: 0}}
                        />
                        <TextField
                            label="Время отправки (чч:мм)"
                            type="time"
                            fullWidth
                            required
                            margin="normal"
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time}
                            inputProps={{step: 60}}
                        />
                    </>
                );
            case 'DRINKS_BEFORE_APPOINTMENT':
                return (
                    <TextField
                        label='За сколько минут до приёма'
                        type='number'
                        fullWidth
                        required
                        margin='normal'
                        value={params.minutesBeforeAppointment ?? ''}
                        onChange={e => {
                            const value = e.target.value === '' ? '' : Math.max(5, Number(e.target.value));
                            updateParam('minutesBeforeAppointment', value);
                        }}
                        inputProps={{min: 5}}
                        error={!!errors.minutesBeforeAppointment}
                        helperText={errors.minutesBeforeAppointment}
                    />
                );
            case 'REMIND_APPOINTMENT_SAME_DAY':
                return (
                    <TextField
                        label='За сколько минут до приёма'
                        type='number'
                        fullWidth
                        required
                        margin='normal'
                        value={params.minutesBeforeAppointment ?? ''}
                        onChange={e => {
                            const value = e.target.value === '' ? '' : Math.max(5, Number(e.target.value));
                            updateParam('minutesBeforeAppointment', value);
                        }}
                        inputProps={{min: 5}}
                        error={!!errors.minutesBeforeAppointment}
                        helperText={errors.minutesBeforeAppointment}
                    />
                );
            case 'PROMO_END':
                return (
                    <>
                        <TextField
                            label="За сколько дней до окончания акции"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.notifyBeforeDays ?? ''}
                            onChange={e => {
                                const value = e.target.value === '' ? '' : Math.max(0, Number(e.target.value));
                                updateParam('notifyBeforeDays', value);
                            }}
                            inputProps={{min: 0}}
                            error={!!errors.notifyBeforeDays}
                            helperText={errors.notifyBeforeDays}
                        />
                        <TextField
                            label="Время отправки"
                            type="time"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{shrink: true}}
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time || 'Указывайте время в часовом поясе клиники'}
                        />
                    </>
                );
            case 'REPEAT_APPOINTMENT':
                return (
                    <>
                        <TextField
                            label="Через сколько дней после приема напомнить"
                            type="number"
                            fullWidth
                            required
                            margin="normal"
                            value={params.daysAfterAppointment ?? 30}
                            onChange={e => {
                                const value = e.target.value === '' ? 30 : Math.max(1, Number(e.target.value));
                                updateParam('daysAfterAppointment', value);
                            }}
                            inputProps={{min: 1}}
                            error={!!errors.daysAfterAppointment}
                            helperText={errors.daysAfterAppointment || 'По умолчанию: 30 дней'}
                        />
                        <TextField
                            label="Время отправки (чч:мм)"
                            type="time"
                            fullWidth
                            required
                            margin="normal"
                            value={formatTime()}
                            onChange={handleTimeChange}
                            error={!!errors.time}
                            helperText={errors.time || 'Указывайте время в часовом поясе клиники'}
                            inputProps={{step: 60}}
                        />
                        <ServiceSelector
                            value={params.serviceIds || []}
                            onChange={(newValue) => updateParam('serviceIds', newValue)}
                            specializations={specializations}
                            label="Услуги для напоминания"
                            required
                            error={!!errors.serviceIds}
                            helperText={errors.serviceIds}
                        />
                    </>
                );
            case 'SURVEY_AFTER_APPOINTMENT':
                return (
                    <>
                        <FormControl fullWidth margin="normal" required error={!!errors.surveyId}>
                            <InputLabel id="survey-label">Опрос</InputLabel>
                            <Select
                                labelId="survey-label"
                                value={params.surveyId || ''}
                                label="Опрос"
                                onChange={e => updateParam('surveyId', e.target.value)}
                            >
                                {surveys.map(survey => (
                                    <MenuItem key={survey.id} value={survey.id}>
                                        {survey.title}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.surveyId && (
                                <FormHelperText error>{errors.surveyId}</FormHelperText>
                            )}
                            <FormHelperText>
                                Опрос будет отправлен пользователю после завершения приема
                            </FormHelperText>
                        </FormControl>
                        <ServiceSelector
                            value={params.serviceIds || []}
                            onChange={(newValue) => updateParam('serviceIds', newValue)}
                            specializations={specializations}
                            label="Услуги"
                            required
                            error={!!errors.serviceIds}
                            helperText={errors.serviceIds || 'Опрос будет отправлен только если в приеме были указанные услуги'}
                        />
                    </>
                );
            default:
                return null;
        }
    }

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{maxWidth: 600}}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {xs: 'column', sm: 'row'}, // Вертикально на xs, горизонтально с sm
                    alignItems: {xs: 'flex-start', sm: 'baseline'},
                    gap: {xs: 0.5, sm: 1},
                    marginRight: '16%'
                }}
                marginBottom={2}
            >
                <Typography variant="h5" component="h2">
                    {isEditing ? `Редактирование триггера:` : 'Создании триггерной рассылки:'}
                </Typography>

                {isEditing ? (
                    <Typography variant="h6" component="h5" sx={{opacity: 0.7}}>
                        {triggerTypes[trigger.type]}
                    </Typography>
                ) : (
                    <FormControl
                        variant="standard"
                        sx={{
                            minWidth: {xs: '100%', sm: 120},
                            marginLeft: {xs: 0, sm: 1},
                            '& .MuiInputBase-root': {
                                fontSize: '1.25rem',
                                fontWeight: 500,
                            },
                            '& .MuiInput-underline:before': {
                                borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
                            },
                        }}
                    >
                        <Select value={type} onChange={handleTypeChange} disableUnderline={false} required>
                            {Object.entries(triggerTypes).map(([key, label]) => (
                                <MenuItem key={key} value={key}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            <TextField
                label="Заголовок"
                value={title}
                onChange={handleTitleChange}
                required
                fullWidth
                margin="normal"
                error={!!errors.title}
                helperText={errors.title}
            />

            <Box sx={{mt: 2}}>
                <Typography variant="subtitle2" sx={{mb: 1}}>
                    Текст сообщения
                </Typography>
                <Editor
                    value={templateText}
                    setValue={handleTemplateTextEditorChange}
                    placeholder="Введите текст сообщения"
                    maxLength={800}
                    onLimit={() => {
                        setErrors(prev => ({
                            ...prev,
                            templateText: 'Максимум 800 символов'
                        }));
                    }}
                />
                <FormHelperText error={!!errors.templateText}>
                    {errors.templateText ||
                        (type === 'SURVEY_AFTER_APPOINTMENT'
                            ? 'Этот текст будет отправлен вместе с кнопкой для начала опроса'
                            : `${getPlainTextLength(templateText || '')}/800 символов`)}
                </FormHelperText>
            </Box>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={type === 'REMIND_BONUS_BURN' ? true : sendToAllBranches}
                        onChange={e => setSendToAllBranches(e.target.checked)}
                        disabled={type === 'REMIND_BONUS_BURN'}
                    />
                }
                label="Разослать всем филиалам"
                sx={{mt: 2}}
            />

            {!sendToAllBranches && (
                <FormControl
                    fullWidth
                    margin="normal"
                    error={!!errors.branchId}
                    required
                >
                    <InputLabel id="branch-label">Филиал</InputLabel>
                    <Select
                        labelId="branch-label"
                        value={selectedBranchId}
                        label="Филиал"
                        onChange={(e) => {
                            setSelectedBranchId(e.target.value);
                            setErrors((prev) => {
                                const newErrors = {...prev};
                                delete newErrors.branchId;
                                return newErrors;
                            });
                        }}
                        required
                    >
                        {branches.map((branch) => (
                            <MenuItem key={branch.id} value={branch.id}>
                                {branch.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors.branchId && (
                        <Box sx={{color: 'error.main', fontSize: '0.75rem', marginTop: '4px'}}>
                            {errors.branchId}
                        </Box>
                    )}
                </FormControl>
            )}

            <Box sx={{border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 2}}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isBonusEnabled}
                            onChange={e => {
                                setIsBonusEnabled(e.target.checked);
                                if (!e.target.checked) {
                                    setPointsCount(0);
                                    setSeparateBonusBurningEnabled(false);
                                    setDaysBeforeBurning('');
                                }
                            }}
                            disabled={type === 'SURVEY_AFTER_APPOINTMENT'}
                        />
                    }
                    label={
                        type === 'SURVEY_AFTER_APPOINTMENT'
                            ? 'Начислить бонусы за прохождение опроса'
                            : 'Начислить бонусы'
                    }
                />
                {type === 'SURVEY_AFTER_APPOINTMENT' && (
                    <FormHelperText sx={{ml: 4, mt: -0.5}}>
                        Бонусы для опросов настраиваются в разделе управления опросами
                    </FormHelperText>
                )}
                {isBonusEnabled && (
                    <Box sx={{ml: 3, mt: 1}}>
                        <TextField
                            label="Количество бонусов"
                            type="number"
                            value={pointsCount > 0 ? pointsCount : ''}
                            onChange={e => {
                                const val = Math.max(0, Number(e.target.value));
                                setPointsCount(val);
                                if (val === 0) setIsBonusEnabled(false);
                            }}
                            fullWidth
                            margin="normal"
                            inputProps={{min: 1}}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={separateBonusBurningEnabled}
                                    onChange={e => {
                                        setSeparateBonusBurningEnabled(e.target.checked);
                                        if (!e.target.checked) setDaysBeforeBurning('');
                                    }}
                                />
                            }
                            label={<>
                                Включить особые правила сгорание бонусов
                            </>}
                            sx={{mt: 1}}
                        />
                        {separateBonusBurningEnabled && (
                            <TextField
                                label="Кол-во дней до сгорания у этого триггера"
                                type="number"
                                value={daysBeforeBurning > 0 ? daysBeforeBurning : ''}
                                onChange={e => {
                                    const val = Math.max(1, Number(e.target.value));
                                    setDaysBeforeBurning(val);
                                }}
                                fullWidth
                                margin="normal"
                                inputProps={{min: 1}}
                                error={!!errors.daysBeforeBurning}
                                helperText={errors.daysBeforeBurning}
                            />
                        )}
                    </Box>
                )}
            </Box>

            {renderParamsFields()}

            {type !== 'SURVEY_AFTER_APPOINTMENT' && (
                <Box marginY={2}>
                    <Button variant="outlined" component="label">
                        Изображение (файл)
                        <input
                            hidden
                            accept="image/*"
                            type="file"
                            onChange={handleImageChange}
                        />
                    </Button>
                    {errors.image && (
                        <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
                            {errors.image}
                        </FormHelperText>
                    )}
                    {imagePreview && (
                        <>
                            <Box mt={2}>
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    style={{maxWidth: '100%', maxHeight: 200, borderRadius: 4}}
                                />
                            </Box>
                            <Button
                                variant="text"
                                color="error"
                                size="small"
                                onClick={handleRemoveImage}
                                sx={{mt: 1}}
                            >
                                Удалить изображение
                            </Button>
                        </>
                    )}
                </Box>
            )}

            <Box marginTop={3} display="flex" justifyContent="flex-end" gap={1}>
                <Button type="submit" variant="contained" disabled={Object.keys(errors).length > 0}>
                    Сохранить
                </Button>
                <Button variant="outlined" onClick={onCancel}>
                    Отмена
                </Button>
            </Box>
        </Box>
    );
}
